<script setup lang="ts">
import KpTextfield from '@/components/kp-textfield.vue'
import { useToast } from '@/composables/toast'
import { instance as api } from '@/helpers/api'
import { errToStr } from '@/helpers/tools'
import { formRules } from '@/helpers/validators'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import KpToolbar from '@/components/kp-toolbar.vue'

const loading = ref(false)
const newEmail = ref('')
const valid = ref(false)
const password = ref('')
const showConfirmation = ref(false)

const { t } = useI18n()

async function onChangeEmail() {
    loading.value = true
    try {
        await api().post('/auth/changeEmail', {
            new_email: newEmail.value,
            password: password.value,
        })
        showConfirmation.value = true
    } catch (e) {
        useToast().add({ text: errToStr(e), color: 'error' })
    } finally {
        loading.value = false
    }
}

function logout() {
    const router = useRouter()
    router.push({ name: 'home' })
    document.location.reload()
}
</script>

<template>
    <div>
        <KpToolbar :title="$t('change_my_email')" icon="fas fa-at" />
        <v-form ref="form" v-model="valid">
            <v-card max-width="600px" class="centered-container">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <kp-textfield
                                v-model:model-value="newEmail"
                                :label="t('new_email')"
                                icon-type="email"
                                :rules="[
                                    formRules.required,
                                    formRules.email,
                                    formRules.maxLength(255),
                                ]"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <kp-textfield
                                v-model:model-value="password"
                                :label="t('old_password')"
                                icon-type="password"
                                :rules="[formRules.required]"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="action white--text"
                        variant="flat"
                        :disabled="!valid || loading"
                        :loading="loading"
                        @click="onChangeEmail"
                    >
                        {{ t('change_my_email') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
        <v-dialog v-model="showConfirmation" :max-width="600">
            <v-card>
                <slot name="title">
                    <v-card-title class="headline">{{ t('confirm_email_sent') }}</v-card-title>
                </slot>
                <slot>
                    <v-card-text>{{
                        t('email_change_successful_txt', { email: newEmail })
                    }}</v-card-text>
                </slot>
                <slot name="actions">
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                            color="action"
                            :loading="loading"
                            @click="logout"
                            prepend-icon="fas fa-check"
                        >
                            {{ t('ok') }}
                        </v-btn>
                    </v-card-actions>
                </slot>
            </v-card>
        </v-dialog>
    </div>
</template>

<style lang="css" scoped>
.centered-container {
    margin-left: auto;
    margin-right: auto;
    margin-top: 1rem;
    padding: 1rem 1rem 1rem 2rem;
}
</style>
